import { Stack } from "@mantine/core";
import { useId } from "react";
import {
  type DateInputOnChangeValue,
  DateRangeInput,
  type DateRangeInputProps
} from "../../../../../mantine/dates/DateRangeInput/DateRangeInput";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import type { BackendDateFormat } from "../../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import { luxonDateTimeToBackendDateOrDateTimeOrNull } from "../../../../../utils/dates/luxonDateTimeToBackendDateOrDateTimeOrNull";
import type { CommonFormFieldItemProps } from "../FormField";
import { FormFieldLabel } from "../FormFieldLabel";
import { HelperText } from "../HelperText/HelperText";
import type { FormFieldTextInputProps } from "./FormFieldTextInput";

interface FormFieldDateRangeInputProps
  extends
    CommonFormFieldItemProps,
    Omit<DateRangeInputProps, "value" | "onChange"> {
  label?: FormFieldTextInputProps["label"];
  error?: string;
  required?: FormFieldTextInputProps["required"];
  value?: [string | null, string | null];
  onChange?: (dateRange: [string | null, string | null]) => void;
  type: "date-range";
  format?: BackendDateFormat;
}

function FormFieldDateRangeInput({
  helpText,
  highlight,
  isBetaFeature,
  label,
  error,
  required,
  type,
  warned,
  value,
  onChange,
  format = "German",
  ...fieldSpecificProps
}: FormFieldDateRangeInputProps) {
  const generatedStartInputId = useId();
  const startInputId =
    fieldSpecificProps.startInputProps?.id ?? generatedStartInputId;
  const parsedValue: DateRangeInputProps["value"] = value
    ? [
        backendDateOrDateTimeToLuxonDateTimeOrNull(value[0]),
        backendDateOrDateTimeToLuxonDateTimeOrNull(value[1])
      ]
    : undefined;

  function handleChange([newStart, newEnd]: DateInputOnChangeValue) {
    onChange?.([
      luxonDateTimeToBackendDateOrDateTimeOrNull(newStart, format),
      luxonDateTimeToBackendDateOrDateTimeOrNull(newEnd, format)
    ]);
  }

  return (
    <Stack gap={0}>
      {label && (
        <FormFieldLabel
          helpText={helpText}
          highlight={highlight}
          htmlFor={startInputId}
          isBetaFeature={isBetaFeature}
          label={label}
          required={required}
        />
      )}
      <DateRangeInput
        {...fieldSpecificProps}
        startInputProps={{
          ...fieldSpecificProps.startInputProps,
          id: startInputId
        }}
        value={parsedValue}
        onChange={handleChange}
      />

      <HelperText error={error} />
    </Stack>
  );
}

export { FormFieldDateRangeInput, FormFieldDateRangeInputProps };
